/**
 * PanelResizeHandle — polished edge resize affordance.
 *
 * Presentation: always-visible vertical pill centered on the panel seam,
 * grows + darkens on hover, accent color + scaleX on drag. A dark pill
 * tooltip ("Drag to resize · click to collapse") fades in after a
 * short hover delay.
 *
 * Controlled component — it renders the visual and handles the gestures
 * (pointer, keyboard, click, dblclick), but the width state lives in the
 * parent. See ResizableSidePanel for the stateful wrapper.
 */
import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useId,
  useRef,
  useState,
} from 'react';
import { mergeClasses } from '@fluentui/react-components';
import {
  CLICK_THRESHOLD_PX,
  KEYBOARD_STEP,
  KEYBOARD_STEP_LARGE,
  clamp,
} from './constants';
import { useStyles } from './PanelResizeHandle.styles';

/**
 * Which edge of the *host panel* the handle lives on. For a panel on the
 * right side of the screen (Assistant, Operation Picker), the handle sits
 * on its LEFT edge and dragging left grows the panel. For a left-side panel
 * the handle is on the RIGHT and dragging right grows it.
 */
export type HandleEdge = 'left' | 'right';

export type PanelResizeHandleProps = {
  /** Which edge of the host panel the handle is attached to. */
  edge: HandleEdge;
  /** Current width of the panel (for `aria-valuenow`). */
  value: number;
  /** Minimum allowed width. */
  min: number;
  /** Maximum allowed width. */
  max: number;
  /** Default width — used by dblclick to reset. */
  defaultValue: number;
  /** Accessible label describing what is being resized (e.g. "Resize Assistant panel"). */
  ariaLabel: string;
  /** Called during a drag with the new width. */
  onResize: (next: number) => void;
  /** Called once at the start of a drag. */
  onResizeStart?: () => void;
  /** Called once at the end of a drag with the final width. */
  onResizeEnd?: (final: number) => void;
  /** Called when the user single-clicks the handle (if collapsible). */
  onCollapseToggle?: () => void;
  /** Called when the user double-clicks the handle. */
  onReset?: () => void;
  /** When true, single-click toggles collapse. */
  collapsible?: boolean;
  /**
   * Optional keyboard shortcut label to display in the tooltip and advertise
   * via `aria-keyshortcuts`. When omitted, no shortcut is shown.
   */
  shortcutLabel?: string;
  /** ARIA keyshortcuts value (W3C format, e.g. "Meta+B"). Paired with shortcutLabel. */
  shortcutAria?: string;
  /** Hide the visible pill (e.g. when the panel is collapsed). Hit area stays live. */
  hidden?: boolean;
  /** Optional extra class for positioning tweaks. */
  className?: string;
};

/**
 * Compute the new width for a given pointer delta, taking edge direction
 * into account. Handle on LEFT edge of a right panel: dragging left
 * (negative dx) grows the panel. Handle on RIGHT edge of a left panel:
 * dragging right (positive dx) grows the panel.
 */
const widthFromDelta = (
  edge: HandleEdge,
  startWidth: number,
  dx: number,
  min: number,
  max: number,
): number => {
  const raw = edge === 'left' ? startWidth - dx : startWidth + dx;
  return clamp(raw, min, max);
};

export const PanelResizeHandle = ({
  edge,
  value,
  min,
  max,
  defaultValue,
  ariaLabel,
  onResize,
  onResizeStart,
  onResizeEnd,
  onCollapseToggle,
  onReset,
  collapsible = true,
  shortcutLabel,
  shortcutAria,
  hidden = false,
  className,
}: PanelResizeHandleProps) => {
  const styles = useStyles();
  const descId = useId();

  const [isHovering, setIsHovering] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Drag state lives in refs so move listeners don't need to re-subscribe.
  const startXRef = useRef(0);
  const startWidthRef = useRef(value);
  const movedPxRef = useRef(0);
  const preDragWidthRef = useRef(value);
  // Timer to disambiguate single-click (collapse) from double-click (reset).
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onPointerDown = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      // Only the primary button should initiate a drag.
      if (e.button !== 0) return;
      e.preventDefault();
      startXRef.current = e.clientX;
      startWidthRef.current = value;
      preDragWidthRef.current = value;
      movedPxRef.current = 0;
      setIsDragging(true);
      onResizeStart?.();
      // Capture so we keep receiving move/up even when the pointer
      // leaves the handle geometry during a fast drag.
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [onResizeStart, value],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      const dx = e.clientX - startXRef.current;
      movedPxRef.current = Math.abs(dx);
      // Only commit resize once movement exceeds click threshold to avoid
      // jitter-induced width changes that also trigger collapse on pointerUp.
      if (movedPxRef.current < CLICK_THRESHOLD_PX) return;
      const next = widthFromDelta(edge, startWidthRef.current, dx, min, max);
      if (next !== value) onResize(next);
    },
    [edge, isDragging, max, min, onResize, value],
  );

  const endDrag = useCallback(
    (commit: boolean) => {
      if (!isDragging) return;
      setIsDragging(false);
      const finalWidth = commit ? value : preDragWidthRef.current;
      if (!commit && finalWidth !== value) {
        onResize(finalWidth);
      }
      onResizeEnd?.(finalWidth);
    },
    [isDragging, onResize, onResizeEnd, value],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent<HTMLDivElement>) => {
      if (!isDragging) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // safe to ignore — pointer may have already been released
      }
      // If the user barely moved, treat as a click → collapse toggle.
      // Don't call endDrag(false) here — that would revert width, but width
      // hasn't changed during a click. Manually clean up drag state instead.
      // Defer via timer so a double-click can cancel it before it fires.
      if (movedPxRef.current < CLICK_THRESHOLD_PX && collapsible && onCollapseToggle) {
        setIsDragging(false);
        onResizeEnd?.(preDragWidthRef.current);
        // Cancel any prior pending collapse (e.g. first click of a double-click).
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        clickTimerRef.current = setTimeout(() => {
          onCollapseToggle();
          clickTimerRef.current = null;
        }, 250);
        return;
      }
      endDrag(true);
    },
    [collapsible, endDrag, isDragging, onCollapseToggle, onResizeEnd],
  );

  const onPointerCancel = useCallback(() => {
    // Browser cancelled the gesture (e.g. user swiped to back-nav) → revert.
    endDrag(false);
  }, [endDrag]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;

      // Map Arrow keys to "grow" / "shrink" per edge so keyboard resize
      // always feels like dragging the handle with the arrow direction.
      const grow = edge === 'left' ? 'ArrowLeft' : 'ArrowRight';
      const shrink = edge === 'left' ? 'ArrowRight' : 'ArrowLeft';

      if (e.key === grow) {
        e.preventDefault();
        onResize(Math.min(max, value + step));
        return;
      }
      if (e.key === shrink) {
        e.preventDefault();
        onResize(Math.max(min, value - step));
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        onResize(min);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        onResize(max);
        return;
      }
      if ((e.key === 'Enter' || e.key === ' ') && collapsible && onCollapseToggle) {
        e.preventDefault();
        onCollapseToggle();
        return;
      }
      if (e.key === 'Escape' && isDragging) {
        e.preventDefault();
        endDrag(false);
      }
    },
    [collapsible, edge, endDrag, isDragging, max, min, onCollapseToggle, onResize, value],
  );

  const onDoubleClick = useCallback(() => {
    // Cancel any pending single-click collapse so dblclick doesn't also toggle.
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    if (onReset) {
      onReset();
    } else {
      onResize(defaultValue);
    }
  }, [defaultValue, onReset, onResize]);

  // Tooltip visibility — show on hover (delayed via CSS), immediately on focus.
  const showTooltip = (isHovering || isFocused) && !isDragging;
  const showTooltipNoDelay = isFocused && !isDragging;

  const tooltipText = collapsible ? (
    <>
      Drag to resize
      <span className={styles.sep} aria-hidden>
        ·
      </span>
      click to collapse
      {shortcutLabel ? (
        <span className={styles.kbd} aria-hidden>
          {shortcutLabel}
        </span>
      ) : null}
    </>
  ) : (
    <>Drag to resize</>
  );

  const srText = collapsible
    ? shortcutLabel
      ? `Drag to resize, or press ${shortcutLabel} to collapse.`
      : 'Drag to resize, or click to collapse.'
    : 'Drag to resize.';

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      aria-valuenow={Math.round(value)}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-describedby={descId}
      aria-keyshortcuts={collapsible && shortcutAria ? shortcutAria : undefined}
      tabIndex={0}
      className={mergeClasses(
        styles.root,
        styles.focusRing,
        edge === 'left' ? styles.rootLeftEdge : styles.rootRightEdge,
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onPointerEnter={() => setIsHovering(true)}
      onPointerLeave={() => setIsHovering(false)}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={onKeyDown}
      onDoubleClick={onDoubleClick}
      data-dragging={isDragging || undefined}
    >
      <span
        aria-hidden
        className={mergeClasses(
          styles.pill,
          hidden && styles.pillHidden,
          !hidden && (isHovering || isFocused) && !isDragging && styles.pillHover,
          !hidden && isDragging && styles.pillActive,
        )}
      />
      <span
        aria-hidden
        className={mergeClasses(
          styles.tooltip,
          edge === 'right' && styles.tooltipRightEdge,
          showTooltip && (showTooltipNoDelay ? styles.tooltipVisibleNoDelay : styles.tooltipVisible),
        )}
      >
        {tooltipText}
      </span>
      <span id={descId} className={styles.srOnly}>
        {srText}
      </span>
    </div>
  );
};
